import { hMacAjax as fetch } from '@/httpService';
import { Storage } from '@/utils/Storage';
import { cloneDeep } from 'lodash';

/**
 * 缓存有效期：7天（秒）
 */
const ONE_WEEK = 7 * 24 * 60 * 60; // 604800

/**
 * 生成缓存 key
 * @param {string} dataSource - data_source 值
 * @returns {string} 缓存 key
 */
export const getCacheKey = (dataSource) => {
    return `cascader_data_${dataSource}`;
};

/**
 * 统一取数（带缓存）
 * @param {string} address - 接口地址
 * @param {string} cacheKey - 缓存 key
 * @returns {Promise<Array>} 数据数组
 */
export const fetchCascaderData = async (address, cacheKey) => {
    // 1. 尝试从缓存获取
    let cachedData = Storage.getStorage(cacheKey);
    if (cachedData?.length > 0) {
        return cachedData;
    }

    // 2. 未命中，请求接口
    try {
        const { code, data } = await fetch.get(address);
        if (code === '00000' && data) {
            Storage.setStorage(cacheKey, data, ONE_WEEK);
            return cloneDeep(data);
        }
        return [];
    } catch (error) {
        console.error('Cascader 数据获取失败:', error);
        return [];
    }
};

/**
 * 清洗树节点：将空的 children 数组设为 undefined
 * 解决 antd-mobile Cascader 最后一级显示"请选择"的问题
 * @param {Array} nodes - 树节点数组
 */
const cleanEmptyChildren = (nodes) => {
    for (const node of nodes) {
        if (node.children && node.children.length === 0) {
            node.children = undefined;
        } else if (node.children && node.children.length > 0) {
            cleanEmptyChildren(node.children);
        }
    }
};

/**
 * 计算树形数据的最大深度（用于自动层级校验）
 * @param {Array} tree - Cascader options 树形数组
 * @returns {number} 最大层级数
 */
export const getMaxDepth = (tree) => {
    if (!tree || tree.length === 0) return 0;
    let max = 1;
    for (const node of tree) {
        if (node.children?.length > 0) {
            max = Math.max(max, 1 + getMaxDepth(node.children));
        }
    }
    return max;
};

/**
 * 通用扁平数据 → 树形结构转换
 * @param {Array} flatData - 扁平数据数组
 * @param {Object} options - 配置选项
 * @param {string} [options.valueField='id'] - 值字段名
 * @param {string} [options.labelField='name'] - 标签字段名
 * @param {string} [options.parentField='parent_code'] - 父级字段名
 * @returns {Array} 树形数组（符合 Cascader options 格式）
 */
export const buildTree = (flatData, options = {}) => {
    const { valueField = 'id', labelField = 'name', parentField = 'parent_code' } = options;

    if (!flatData || !Array.isArray(flatData) || flatData.length === 0) {
        return [];
    }

    const map = {};
    const tree = [];

    // 第一步：创建映射表并初始化节点
    flatData.forEach((item) => {
        map[item[valueField]] = {
            value: item[valueField],
            label: item[labelField],
            children: []
        };
    });

    // 第二步：构建树形结构
    flatData.forEach((item) => {
        const node = map[item[valueField]];
        const parentId = item[parentField];

        // 父级为 '0' 或不存在父级 → 顶级节点
        if (!parentId || parentId === '0' || !map[parentId]) {
            tree.push(node);
        } else {
            if (map[parentId].children) {
                map[parentId].children.push(node);
            }
        }
    });

    cleanEmptyChildren(tree);
    return tree;
};

/**
 * 地区专用转换 + 层级裁剪
 * 字段约定：code(值)、name(标签)、parent_code(父级)、deep(层级)
 * @param {Array} flatData - 扁平地区数据
 * @param {number} maxDeep=3 - 最大层级深度（1=仅省, 2=省市, 3=省市区县）
 * @returns {Array} 裁剪后的树形数组
 */
export const buildAreaTree = (flatData, maxDeep = 3) => {
    if (!flatData || !Array.isArray(flatData) || flatData.length === 0) {
        return [];
    }

    const map = {};
    const tree = [];

    // 第一步：创建映射表
    flatData.forEach((item) => {
        map[item.code] = {
            value: item.code,
            label: item.name,
            deep: item.deep,
            parent_code: item.parent_code,
            children: []
        };
    });

    // 第二步：构建树形结构（带层级裁剪）
    flatData.forEach((item) => {
        const node = map[item.code];
        const currentDeep = item.deep;

        // 超过最大深度的节点直接丢弃
        if (currentDeep > maxDeep) {
            return;
        }

        // 达到或超过最大深度的节点标记为叶子节点
        if (currentDeep >= maxDeep) {
            node.children = undefined; // 叶子节点的 children 设为 undefined
        }

        // 父级编码为 '0' 表示顶级节点（省份）
        if (item.parent_code === '0') {
            tree.push(node);
        } else {
            // 找到父节点并添加到其 children 数组中
            const parent = map[item.parent_code];
            if (parent && parent.children) {
                parent.children.push(node);
            }
        }
    });

    return tree;
};
